import {inject, Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {CardataWizardCommunicationService} from '@ucb2b/cardata-wizard';
import {BehaviorSubject, EMPTY, forkJoin, Observable, of} from 'rxjs';
import {catchError, first, retry, tap} from 'rxjs/operators';
import {CarDataConfig, CarDataIn, User,} from '@ucb2b/cardata-wizard/typings/cardata';
import {APP_CONFIG} from "../app.config";

/**
 * Init communication with CardataWizardCommunicationService.
 */
@Injectable({
  providedIn: 'root',
})
export class TdsWizardService {
  private http = inject(HttpClient);
  private cardataWizardCommunicationService = inject(CardataWizardCommunicationService);

  private token = '';
  private readonly appConfig = inject(APP_CONFIG)

  private tdsIsReady$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private initializing = false;

  constructor() {
    this.init();
  }

  /**
   * Returns the TDS readiness state.
   * If TDS previously failed, resets to null and retries initialization.
   */
  public tdsIsReady(): Observable<boolean | null> {
    if (this.tdsIsReady$.value === false && !this.initializing) {
      this.tdsIsReady$.next(null);
      this.init();
    }
    return this.tdsIsReady$;
  }

  private init() {
    this.initializing = true;
    const token$ = this.getCardataToken() as Observable<string>;
    const init = forkJoin(token$.pipe(first())) as Observable<any>;

    init.subscribe(
      (response) => {
        const myCardata = {} as CarDataIn;
        const myCardataConfig = { resetStoreOnPublish: true } as CarDataConfig;
        myCardata.baseUrl = this.appConfig.tdsBaseUrl;
        myCardata.config = myCardataConfig;
        myCardata.user = {
          market: this.appConfig.tdsMarket,
          lang: this.appConfig.tdsLanguage,
          authToken: response[0],
        } as User;

        this.cardataWizardCommunicationService.setInput(myCardata);
        this.tdsIsReady$.next(true);
        this.initializing = false;
      },
      (error: any) => {
        console.error(error);
        this.tdsIsReady$.next(false);
        this.initializing = false;
      },
    );
  }

  /**
   * Get an authorization token to perform Cardata API requests against the Typdata service.
   *
   * Uses the `X-Skip-Error-Interceptor` header to bypass the global {@link ErrorInterceptor}.
   * This is necessary because the token fetch may fail due to race conditions and is retried
   * up to 4 times. Without the skip header, each failed attempt would trigger a user-facing
   * error notification via the global error handler. Instead, failures are handled locally:
   * on final failure the TDS wizard is marked as unavailable (`tdsIsReady$ = false`) and
   * no value is emitted, allowing the app to degrade gracefully.
   */
  private getCardataToken(): Observable<string> {
    if (this.token) {
      return of(this.token);
    } else {
      return this.http.get('/api/cardata/token', { responseType: 'text', headers: { 'X-Skip-Error-Interceptor': 'true' } }).pipe(
        retry(4), // retry to account for race conditions
        tap((token: string) => (this.token = token)),
        catchError((error) => {
          console.error(error);
          this.tdsIsReady$.next(false);
          return EMPTY; // don't emit a value — prevents the success handler from running
        }),
      );
    }
  }
}
